import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Notification from 'flarum/forum/components/Notification';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import PostControls from 'flarum/forum/utils/PostControls';
import EditPostComposer from 'flarum/forum/components/EditPostComposer';

// =============================================
// 1. Content Blocked Notification
// =============================================
class ContentBlockedNotification extends Notification {
    icon() {
        return 'fas fa-shield-alt';
    }

    href() {
        var notification = this.attrs.notification;
        var subject = notification.subject();
        if (subject && subject.discussion) {
            return app.route.discussion(subject.discussion(), subject.number());
        }
        return '#';
    }

    content() {
        return '您的帖子因内容审核未通过已被暂时隐藏，请编辑修改后将自动重新审核。';
    }
}

// =============================================
// 2. External Link Warning Modal
// =============================================
class ExternalLinkModal extends Modal {
    className() {
        return 'ExternalLinkModal Modal--small';
    }

    title() {
        return [
            m('i.fas.fa-exclamation-triangle', { style: { color: '#e67e22', marginRight: '8px' } }),
            '外部链接安全提醒',
        ];
    }

    content() {
        var url = this.attrs.url || '';
        var domain = '';
        try { domain = new URL(url).hostname; } catch(e) { domain = url; }

        return m('.Modal-body', [
            m('.ExternalLink-warning', { style: { padding: '16px 0' } }, [
                m('p', { style: { fontSize: '15px', marginBottom: '12px' } }, [
                    '您即将离开 ',
                    m('strong', '三局两胜TRPG玩家社区'),
                    '，前往外部网站：',
                ]),
                m('.ExternalLink-url', {
                    style: {
                        background: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        padding: '10px 14px',
                        wordBreak: 'break-all',
                        fontSize: '13px',
                        color: '#495057',
                        marginBottom: '16px',
                    }
                }, [
                    m('i.fas.fa-external-link-alt', { style: { marginRight: '6px', color: '#6c757d' } }),
                    domain,
                ]),
                m('.ExternalLink-disclaimer', {
                    style: {
                        background: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '6px',
                        padding: '12px 14px',
                        fontSize: '13px',
                        color: '#856404',
                        lineHeight: '1.6',
                    }
                }, [
                    m('p', { style: { margin: '0 0 6px 0', fontWeight: 'bold' } }, '安全提示：'),
                    m('ul', { style: { margin: '0', paddingLeft: '18px' } }, [
                        m('li', '外部网站的内容与本社区无关，不代表本社区立场。'),
                        m('li', '请注意保护个人隐私，不要在不信任的网站输入敏感信息。'),
                        m('li', '如遇到违法或有害内容，请及时关闭页面。'),
                    ]),
                ]),
            ]),
            m('.ExternalLink-actions', { style: { display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '12px' } }, [
                m(Button, {
                    className: 'Button',
                    onclick: function() { app.modal.close(); },
                }, '取消'),
                m(Button, {
                    className: 'Button Button--primary',
                    onclick: function() {
                        window.open(url, '_blank', 'noopener,noreferrer');
                        app.modal.close();
                    },
                }, '继续访问'),
            ]),
        ]);
    }
}

// =============================================
// Trusted domains that don't trigger the warning
// =============================================
var TRUSTED_DOMAINS = [
    'bo3studio.com',
    'forum.bo3studio.com',
];

function isTrustedUrl(url) {
    try {
        var hostname = new URL(url).hostname.toLowerCase();
        for (var i = 0; i < TRUSTED_DOMAINS.length; i++) {
            var d = TRUSTED_DOMAINS[i];
            if (hostname === d || hostname.endsWith('.' + d)) {
                return true;
            }
        }
    } catch (e) {}
    return false;
}

// =============================================
// Initializer
// =============================================
app.initializers.add('bo3studio/flarum-content-guard', function() {
    app.notificationComponents.contentBlocked = ContentBlockedNotification;

    // --- Allow editing hidden posts (for moderation-hidden posts) ---
    // Flarum core's PostControls.moderationControls skips edit button for hidden posts.
    // We add it back so authors can fix their content and trigger re-moderation.
    extend(PostControls, 'moderationControls', function(items, post) {
        // Only add if: post is hidden, user can edit, and edit button not already present
        if (post.isHidden() && post.canEdit() && !items.has('edit')) {
            items.add('edit',
                m(Button, {
                    icon: 'fas fa-pencil-alt',
                    onclick: function() {
                        var deferred = PostControls.editAction.call(post);
                    },
                }, '编辑（修改后重新审核）')
            );
        }
    });

    // --- External link interception ---
    document.body.addEventListener('click', function(e) {
        // Find the closest <a> element
        var link = e.target.closest('a[href]');
        if (!link) return;

        var href = link.getAttribute('href');
        if (!href) return;

        // Only intercept http/https links
        if (href.indexOf('http://') !== 0 && href.indexOf('https://') !== 0) return;

        // Skip trusted domains
        if (isTrustedUrl(href)) return;

        // Skip links in admin area or navigation
        if (link.closest('.Header, .Navigation, .Sidebar, .Modal')) return;

        // Intercept: prevent default navigation and show modal
        e.preventDefault();
        e.stopPropagation();

        app.modal.show(ExternalLinkModal, { url: href });
    }, true);

    // --- Moderation error style override ---
    var origRequestError = app.requestError;
    app.requestError = function(error) {
        if (error && error.status === 422 && error.response && error.response.errors) {
            var errors = error.response.errors;
            var isModerationError = false;

            for (var i = 0; i < errors.length; i++) {
                var detail = errors[i].detail || '';
                if (detail.indexOf('\u654F\u611F\u8BCD') !== -1 || detail.indexOf('\u5185\u5BB9\u5BA1\u6838') !== -1) {
                    isModerationError = true;
                    break;
                }
            }

            if (isModerationError) {
                var detail = errors[0].detail || '';
                app.alerts.show(
                    { type: 'warning', controls: [], className: 'ContentGuard-alert' },
                    [
                        m('span.ContentGuard-alertIcon', '\u26D4'),
                        ' ',
                        m('strong', '\u5185\u5BB9\u5BA1\u6838\u63D0\u9192'),
                        m('span', ' \u2014 ' + detail),
                    ]
                );
                error.alert = null;
                return;
            }
        }

        if (origRequestError) {
            origRequestError.call(app, error);
        }
    };
});
